"use client";

// Root error boundary for the web app (Next.js App Router convention).
// Replaces the root layout when something fails outside any nested boundary
// — so it must render its own <html> and <body>.

import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { reportError } from "@eventify/shared/lib/errorReporter";
import { getCurrentWebContext, detectEnvironment } from "../lib/errorContext";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
    reportError(error, {
      surface: "web",
      source: "boundary",
      ...getCurrentWebContext(),
      extra: { digest: error.digest ?? null, scope: "global" },
    }, { environment: detectEnvironment() });
  }, [error]);

  return (
    // Mirrors the root layout's lang+dir so a global error screen still
    // identifies its language to screen readers (jsx-a11y/html-has-lang).
    <html lang="he" dir="rtl">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          background: "#fbf8ff",
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "#ffffff",
            borderRadius: 24,
            padding: 32,
            boxShadow: "0 10px 40px rgba(131, 54, 178, 0.08)",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              background: "#f4f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: "#8336b2", fontSize: 28 }}
            >
              warning
            </span>
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#1a1b23",
              margin: "0 0 8px 0",
              fontFamily: "'Plus Jakarta Sans', Inter, Rubik, sans-serif",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "#4d4351",
              lineHeight: 1.5,
              margin: "0 0 24px 0",
            }}
          >
            We&apos;ve been notified and are looking into it. Try reloading the page.
          </p>
          <button
            onClick={() => reset()}
            style={{
              background: "linear-gradient(135deg, #8336b2 0%, #9e51cd 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: 999,
              padding: "14px 24px",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
              boxShadow: "0 8px 24px rgba(131, 54, 178, 0.25)",
            }}
          >
            Reload
          </button>
        </div>
      </body>
    </html>
  );
}
